import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Autocomplete,
    TextField,
    CircularProgress,
    Typography,
    Box,
    Avatar
} from '@mui/material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../api/axios';
import { Citizen } from '../../types/citizen';
import { SubLeader } from '../../types/sub_leader'; // Ensure this type exists or interface it locally if needed

interface AssignSubLeaderDialogProps {
    open: boolean;
    onClose: () => void;
    citizen: Citizen | null;
    onSuccess?: () => void;
}

const AssignSubLeaderDialog: React.FC<AssignSubLeaderDialogProps> = ({ open, onClose, citizen, onSuccess }) => {
    const queryClient = useQueryClient();
    const [selectedSubLeader, setSelectedSubLeader] = useState<SubLeader | null>(null);
    const [inputValue, setInputValue] = useState('');

    // Fetch Sub Leaders (Searchable)
    const { data: subLeaders = [], isLoading } = useQuery({
        queryKey: ['sub-leaders-search', inputValue],
        queryFn: async () => {
            // If we have a search term, filter by it. Otherwise fetch first page.
            // Ideally this should look up "all" or search via API.
            // For now assuming /tenant/sub-leaders returns pagination.
            const params: any = { per_page: 50 };
            if (inputValue) params.search = inputValue;

            const res = await api.get('/tenant/sub-leaders', { params });
            // Adjust based on actual API response structure (data.data or just data)
            return res.data?.data?.data || res.data?.data || [];
        },
        enabled: open,
    });

    // Reset state when dialog opens
    useEffect(() => {
        if (open && citizen) {
            // If citizen already has a sub_leader assigned, potentially pre-fill
            // Assuming sub_leader relation exists on citizen
            if ((citizen as any).sub_leader) {
                setSelectedSubLeader((citizen as any).sub_leader);
            } else {
                setSelectedSubLeader(null);
            }
        }
    }, [open, citizen]);

    const mutation = useMutation({
        mutationFn: async (subLeaderId: number | null) => {
            if (!citizen) return;
            // Assuming the API accepts sub_leader_id in the PUT request
            return api.put(`/tenant/citizens/${citizen.id}`, {
                sub_leader_id: subLeaderId
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['all-citizens'] });
            queryClient.invalidateQueries({ queryKey: ['citizen', citizen?.id] });
            if (onSuccess) onSuccess();
            onClose();
        },
        onError: (error) => {
            console.error("Failed to assign sub leader", error);
            // Could add snackbar logic here if passed down
            alert('Failed to update sub leader assignment.');
        }
    });

    const handleSave = () => {
        mutation.mutate(selectedSubLeader?.id || null);
    };

    if (!citizen) return null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Assign Sub Leader</DialogTitle>
            <DialogContent>
                <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                        Assigning Sub Leader for: <strong>{citizen.name}</strong>
                    </Typography>
                </Box>

                <Autocomplete
                    options={subLeaders}
                    getOptionLabel={(option: SubLeader) => `${option.first_name} ${option.last_name}${option.position ? ` - ${option.position}` : ''} (${option.phone})`}
                    loading={isLoading}
                    value={selectedSubLeader}
                    onChange={(event, newValue) => setSelectedSubLeader(newValue)}
                    onInputChange={(event, newInputValue) => {
                        setInputValue(newInputValue);
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Search Sub Leader"
                            fullWidth
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <React.Fragment>
                                        {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                        {params.InputProps.endAdornment}
                                    </React.Fragment>
                                ),
                            }}
                        />
                    )}
                    renderOption={(props, option) => (
                        <li {...props}>
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                <Typography variant="body1">{option.first_name} {option.last_name}</Typography>
                                {option.position && (
                                    <Typography variant="caption" color="primary" sx={{ fontWeight: 600 }}>{option.position}</Typography>
                                )}
                                <Typography variant="caption" color="text.secondary">{option.phone} • {option.constituency?.name || 'No Location'}</Typography>
                            </Box>
                        </li>
                    )}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="inherit">Cancel</Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    color="primary"
                    disabled={mutation.isPending}
                >
                    {mutation.isPending ? 'Saving...' : 'Save Assignment'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AssignSubLeaderDialog;
